// The application's navigation structure, in display order.
//
// Extracted from RegureSidebar so it is not owned by a component. The
// sidebar renders it, and the post-login redirect resolves a landing page
// from it (see getLandingPath) — previously that redirect carried its own
// hardcoded per-role paths, which drifted: a broker was sent to
// /portal/my-clients while the first item in their own nav was
// /portal/my-policies, so the app opened on a page that was not where the
// sidebar said they were.
//
// Labels are i18n keys, never literal strings.
// Legacy env vars — used as fallback when the tenant config hasn't loaded
// yet (e.g. immediately after login, or if the backend route is unavailable).
// The per-tenant config takes priority once present.
const LEGACY_ENABLE_COMMS        = process.env.REACT_APP_ENABLE_CHAT_VIDEO_ESIGN === 'true';
const LEGACY_ENABLE_EXPERIMENTAL = process.env.REACT_APP_ENABLE_EXPERIMENTAL_MODULES === 'true';
const LEGACY_ENABLE_CLIENTS      = process.env.REACT_APP_ENABLE_CLIENTS === 'true';

export const NAV_ITEMS = [
  // Portal section (for policyholder and broker roles)
  {
    section: 'PORTALS',
    sectionKey: 'nav.sectionPortals',
    items: [
      { id: 'my-policies', labelKey: 'nav.myPolicies', icon: 'file-check', path: '/portal/my-policies' },
      { id: 'my-claims', labelKey: 'nav.myClaims', icon: 'shield', path: '/portal/my-claims' },
      { id: 'my-clients', labelKey: 'nav.myClients', icon: 'users', path: '/portal/my-clients' },
      { id: 'my-commissions', labelKey: 'nav.myCommissions', icon: 'percent', path: '/portal/my-commissions' },
      { id: 'my-documents', labelKey: 'nav.myDocuments', icon: 'file-text', path: '/portal/my-documents' },
      { id: 'my-profile', labelKey: 'nav.myProfile', icon: 'settings', path: '/portal/my-profile' },
    ],
  },
  {
    section: 'PLATFORM',
    sectionKey: 'nav.sectionPlatform',
    items: [
      { id: 'platform', labelKey: 'nav.platform', icon: 'grid', path: '/platform' },
      { id: 'tenants', labelKey: 'nav.tenantManagement', icon: 'settings', path: '/platform/tenants' },
      { id: 'onboard-org', labelKey: 'nav.onboardOrg', icon: 'building', path: '/orgs/onboard' },
    ],
  },
  {
    section: 'INTELLIGENCE',
    sectionKey: 'nav.sectionIntelligence',
    items: [
      { id: 'dashboard', labelKey: 'nav.dashboard', icon: 'grid', path: '/dashboard' },
      { id: 'claims-analytics', labelKey: 'nav.claimsAnalytics', icon: 'bar-chart', path: '/claims-analytics' },
      { id: 'documents', labelKey: 'nav.documents', icon: 'file-text', path: '/unmatched-documents', hidden: !LEGACY_ENABLE_EXPERIMENTAL },
      { id: 'workflows', labelKey: 'nav.workflows', icon: 'git-branch', path: '/workflow-rules' },
      { id: 'risk-signals', labelKey: 'nav.riskSignals', icon: 'alert-triangle', path: '/risk-signals' },
    ],
  },
  {
    section: 'OPERATIONS',
    sectionKey: 'nav.sectionOperations',
    items: [
      { id: 'clients', labelKey: 'nav.clients', icon: 'users', path: '/clients', hidden: !LEGACY_ENABLE_CLIENTS },
      { id: 'claims', labelKey: 'nav.claims', icon: 'shield', path: '/claims' },
      { id: 'policies', labelKey: 'nav.policies', icon: 'file-check', path: '/policies' },
      { id: 'offers', labelKey: 'nav.offers', icon: 'calculator', path: '/offers' },
      { id: 'invoicing', labelKey: 'nav.invoicing', icon: 'receipt', path: '/invoicing' },
      { id: 'commissions', labelKey: 'nav.commissions', icon: 'percent', path: '/commissions' },
    ],
  },
  {
    section: 'REINSURANCE',
    sectionKey: 'nav.sectionReinsurance',
    items: [
      // The module landing page. First in the section because it is the
      // overview the other ten items drill into.
      { id: 're-overview',    labelKey: 'nav.reOverview',    icon: 'grid',        path: '/reinsurance' },
      { id: 're-submissions', labelKey: 'nav.reSubmissions', icon: 'file-text',   path: '/reinsurance/submissions' },
      { id: 're-placement',   labelKey: 'nav.rePlacement',   icon: 'git-branch',  path: '/reinsurance/placement' },
      { id: 're-closings',    labelKey: 'nav.reClosings',    icon: 'file-check',  path: '/reinsurance/closings' },
      { id: 're-claims',      labelKey: 'nav.reClaims',      icon: 'shield',      path: '/reinsurance/claims' },
      { id: 're-portfolio',   labelKey: 'nav.rePortfolio',   icon: 'bar-chart',   path: '/reinsurance/portfolio' },
      // Second wave (2026-09). Order matches the approved design: the five
      // original items, then treaties → accounting → recoveries →
      // counterparties → renewals. Each is hidden unless its own tenant
      // flag is on, so a tenant that has not opted in sees no change.
      { id: 're-treaties',       labelKey: 'nav.reTreaties',       icon: 'layers',     path: '/reinsurance/treaties' },
      { id: 're-accounting',     labelKey: 'nav.reAccounting',     icon: 'database',   path: '/reinsurance/accounting' },
      { id: 're-recoveries',     labelKey: 'nav.reRecoveries',     icon: 'refresh-cw', path: '/reinsurance/recoveries' },
      { id: 're-counterparties', labelKey: 'nav.reCounterparties', icon: 'users',      path: '/reinsurance/counterparties' },
      { id: 're-renewals',       labelKey: 'nav.reRenewals',       icon: 'calendar',   path: '/reinsurance/renewals' },
    ],
  },
  {
    section: 'COMMUNICATIONS',
    sectionKey: 'nav.sectionCommunications',
    items: [
      { id: 'chat', labelKey: 'nav.chat', icon: 'message-circle', path: '/chat', hidden: !LEGACY_ENABLE_COMMS },
      { id: 'video', labelKey: 'nav.videoMeetings', icon: 'video', path: '/group-video-call', hidden: !LEGACY_ENABLE_COMMS },
      { id: 'esign', labelKey: 'nav.esignatures', icon: 'pen-tool', path: '/e-signatures', hidden: !LEGACY_ENABLE_COMMS },
    ],
  },
  {
    section: 'COMPLIANCE',
    sectionKey: 'nav.sectionCompliance',
    items: [
      { id: 'audit', labelKey: 'nav.auditTrail', icon: 'lock', path: '/audit-logs' },
      { id: 'integrity', labelKey: 'nav.auditIntegrity', icon: 'shield', path: '/audit-integrity' },
      { id: 'compliance', labelKey: 'nav.compliance', icon: 'check-circle', path: '/compliance-reports', hidden: !LEGACY_ENABLE_EXPERIMENTAL },
      { id: 'residency', labelKey: 'nav.dataResidency', icon: 'globe', path: '/data-residency', hidden: !LEGACY_ENABLE_EXPERIMENTAL },
      { id: 'dsar', labelKey: 'nav.dsar', icon: 'user-check', path: '/dsar-requests', hidden: !LEGACY_ENABLE_EXPERIMENTAL },
      { id: 'ai-governance', labelKey: 'nav.aiGovernance', icon: 'cpu', path: '/ai-governance', hidden: !LEGACY_ENABLE_EXPERIMENTAL },
    ],
  },
  {
    section: 'ADMINISTRATION',
    sectionKey: 'nav.sectionAdministration',
    items: [
      { id: 'team', labelKey: 'nav.teamManagement', icon: 'users', path: '/users' },
      { id: 'brokers', labelKey: 'nav.brokers', icon: 'briefcase', path: '/brokers' },
      { id: 'carriers', labelKey: 'nav.riskCarriers', icon: 'building', path: '/risk-carriers' },
      { id: 'bordereaux', labelKey: 'nav.bordereaux', icon: 'file-text', path: '/bordereaux' },
      { id: 'apikeys', labelKey: 'nav.apiKeys', icon: 'key', path: '/api-keys', hidden: !LEGACY_ENABLE_EXPERIMENTAL },
      { id: 'webhooks', labelKey: 'nav.webhooks', icon: 'link', path: '/webhooks', hidden: !LEGACY_ENABLE_EXPERIMENTAL },
      { id: 'integrations', labelKey: 'nav.integrations', icon: 'link', path: '/integrations' },
      { id: 'settings', labelKey: 'nav.accountSettings', icon: 'settings', path: '/account-settings' },
    ],
  },
];

export default NAV_ITEMS;
